/**
 * useDetection — React hook for running marker detection loop.
 *
 * Captures video frames at reduced resolution, runs ArUco and QR detection,
 * and reports unified results. Uses requestAnimationFrame with frame skipping
 * for performance.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import type { RefObject } from 'react';
import { ArucoDetector } from './ArucoDetector';
import { QrDetector } from './QrDetector';
import { BarcodeDetector } from './BarcodeDetector';
import type {
  DetectedMarker,
  DetectionResult,
  UseDetectionOptions,
  UseDetectionReturn,
} from './types';

const DEFAULT_FRAME_SKIP = 3;
const DEFAULT_MAX_DETECTION_WIDTH = 640;

export function useDetection(
  videoRef: RefObject<HTMLVideoElement | null>,
  options: UseDetectionOptions = {},
): UseDetectionReturn {
  const {
    enabled = true,
    frameSkip = DEFAULT_FRAME_SKIP,
    maxDetectionWidth = DEFAULT_MAX_DETECTION_WIDTH,
    enableAruco = true,
    enableQr = true,
    enableBarcode = false,
  } = options;

  const [detections, setDetections] = useState<DetectionResult | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [fps, setFps] = useState(0);

  // Refs for the detection loop (avoid re-creating on every render)
  const arucoDetectorRef = useRef<ArucoDetector | null>(null);
  const qrDetectorRef = useRef<QrDetector | null>(null);
  const barcodeDetectorRef = useRef<BarcodeDetector | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rafRef = useRef<number>(0);
  const frameCountRef = useRef(0);
  const fpsCountRef = useRef(0);
  const fpsTimestampRef = useRef(0);
  const barcodeMarkersRef = useRef<DetectedMarker[]>([]);

  // Initialize detectors lazily
  const getArucoDetector = useCallback(() => {
    if (!arucoDetectorRef.current) {
      arucoDetectorRef.current = new ArucoDetector();
    }
    return arucoDetectorRef.current;
  }, []);

  const getQrDetector = useCallback(() => {
    if (!qrDetectorRef.current) {
      qrDetectorRef.current = new QrDetector();
    }
    return qrDetectorRef.current;
  }, []);

  const getBarcodeDetector = useCallback(() => {
    if (!barcodeDetectorRef.current) {
      barcodeDetectorRef.current = new BarcodeDetector();
    }
    return barcodeDetectorRef.current;
  }, []);

  // Initialize offscreen canvas lazily
  const getCanvas = useCallback(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      ctxRef.current = canvasRef.current.getContext('2d', {
        willReadFrequently: true,
      });
    }
    return { canvas: canvasRef.current, ctx: ctxRef.current };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsDetecting(false);
      setDetections(null);
      return;
    }

    let running = true;

    const detectFrame = () => {
      if (!running) return;

      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.videoWidth === 0) {
        rafRef.current = requestAnimationFrame(detectFrame);
        return;
      }

      frameCountRef.current++;

      // Frame skipping
      if (frameCountRef.current % frameSkip !== 0) {
        rafRef.current = requestAnimationFrame(detectFrame);
        return;
      }

      setIsDetecting(true);

      const vw = video.videoWidth;
      const vh = video.videoHeight;

      // Calculate detection resolution (downscale for performance)
      const scale = Math.min(maxDetectionWidth / vw, 1);
      const dw = Math.round(vw * scale);
      const dh = Math.round(vh * scale);

      const { canvas, ctx } = getCanvas();
      if (!ctx) {
        rafRef.current = requestAnimationFrame(detectFrame);
        return;
      }

      // Resize canvas if needed
      if (canvas.width !== dw || canvas.height !== dh) {
        canvas.width = dw;
        canvas.height = dh;
      }

      // Draw video frame to offscreen canvas
      ctx.drawImage(video, 0, 0, dw, dh);
      const imageData = ctx.getImageData(0, 0, dw, dh);

      // Run synchronous detectors and merge results
      const markers: DetectedMarker[] = [];

      if (enableAruco) {
        const arucoDetector = getArucoDetector();
        markers.push(...arucoDetector.detect(imageData));
      }

      if (enableQr) {
        const qrDetector = getQrDetector();
        markers.push(...qrDetector.detect(imageData));
      }

      // Add barcode markers from previous async detection
      if (enableBarcode) {
        markers.push(...barcodeMarkersRef.current);
      }

      // Start async barcode detection (non-blocking)
      if (enableBarcode) {
        const barcodeDetector = getBarcodeDetector();
        barcodeDetector
          .detect(imageData)
          .then((barcodeMarkers) => {
            barcodeMarkersRef.current = barcodeMarkers;
          })
          .catch(() => {
            // Silently handle errors (detection failed)
            barcodeMarkersRef.current = [];
          });
      }

      const now = performance.now();

      setDetections({
        markers,
        timestamp: now,
        frameWidth: dw,
        frameHeight: dh,
      });

      // FPS tracking
      fpsCountRef.current++;
      if (now - fpsTimestampRef.current >= 1000) {
        setFps(fpsCountRef.current);
        fpsCountRef.current = 0;
        fpsTimestampRef.current = now;
      }

      rafRef.current = requestAnimationFrame(detectFrame);
    };

    fpsTimestampRef.current = performance.now();
    fpsCountRef.current = 0;
    frameCountRef.current = 0;
    rafRef.current = requestAnimationFrame(detectFrame);

    return () => {
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      setIsDetecting(false);
    };
  }, [
    enabled,
    frameSkip,
    maxDetectionWidth,
    enableAruco,
    enableQr,
    enableBarcode,
    videoRef,
    getArucoDetector,
    getQrDetector,
    getBarcodeDetector,
    getCanvas,
  ]);

  return { detections, isDetecting, fps };
}
